import { faSpinner } from '@fortawesome/pro-solid-svg-icons/faSpinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cn } from '@thinkalpha/ui-core/utils';
import React, { forwardRef } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from 'src/components/ui';
import { ScrollArea } from 'src/components/ui/scroll-area';
import { useEvent } from 'src/hooks/useEvent';

// Helper function for mouse down handling
const handleItemMouseDown = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
};

// Root menu component
export interface SymbolPickerMenuProps {
    open: boolean;
    children: React.ReactNode;
}
export const SymbolPickerMenu = ({ open, children }: SymbolPickerMenuProps) => {
    return <Popover open={open}>{children}</Popover>;
};

// Menu trigger component
export interface SymbolPickerMenuTriggerProps {
    asChild?: boolean;
    children: React.ReactNode;
}
export const SymbolPickerMenuTrigger = ({ asChild = true, children }: SymbolPickerMenuTriggerProps) => {
    return <PopoverTrigger asChild={asChild}>{children}</PopoverTrigger>;
};

export interface SymbolPickerMenuContentProps {
    className?: string;
    align?: 'start' | 'center' | 'end';
    side?: 'top' | 'right' | 'bottom' | 'left';
    children: React.ReactNode;
}
export const SymbolPickerMenuContent = forwardRef<
    React.ElementRef<typeof PopoverContent>,
    SymbolPickerMenuContentProps
>(({ className, align = 'start', side = 'bottom', children }, ref) => {
    return (
        <PopoverContent
            ref={ref}
            className={cn('p-0', className)}
            onOpenAutoFocus={(e) => {
                e.stopPropagation();
                e.preventDefault();
            }}
            onCloseAutoFocus={(e) => {
                e.stopPropagation();
                e.preventDefault();
            }}
            align={align}
            side={side}
        >
            {children}
        </PopoverContent>
    );
});

SymbolPickerMenuContent.displayName = 'SymbolPickerMenuContent';

export interface SymbolPickerMenuListProps {
    className?: string;
    children: React.ReactNode;
}
export const SymbolPickerMenuList = ({ className, children }: SymbolPickerMenuListProps) => {
    return (
        <ScrollArea className={cn('h-auto rounded-lg', className)}>
            <div className="first:pt-0.5 last:pb-0.5">{children}</div>
        </ScrollArea>
    );
};

export interface SymbolPickerMenuHeaderProps {
    className?: string;
    children: React.ReactNode;
}
export const SymbolPickerMenuHeader = ({ className, children }: SymbolPickerMenuHeaderProps) => {
    return <div className={cn('h-3 p-0.5 text-sm leading-[95%] font-medium text-gray-400', className)}>{children}</div>;
};

export interface SymbolPickerMenuItemProps {
    className?: string;
    selected?: boolean;
    onSelect?: () => void;
    onMouseEnter?: () => void;
    children: React.ReactNode;
}
export const SymbolPickerMenuItem = ({
    className,
    selected = false,
    onSelect,
    onMouseEnter,
    children,
}: SymbolPickerMenuItemProps) => {
    const handleClick = useEvent(() => {
        onSelect?.();
    });

    const handleMouseEnter = useEvent(() => {
        onMouseEnter?.();
    });

    return (
        <div
            className={cn(
                'flex h-4 w-full cursor-pointer items-center gap-2 px-2 text-sm hover:bg-gray-600',
                className,
                selected && 'bg-gray-600',
            )}
            onMouseDown={handleItemMouseDown}
            onMouseEnter={handleMouseEnter}
            onClick={handleClick}
        >
            {children}
        </div>
    );
};

export interface SymbolPickerMenuSpinnerProps {
    className?: string;
}
export const SymbolPickerMenuSpinner = ({ className }: SymbolPickerMenuSpinnerProps) => {
    return (
        <div className={cn('flex h-8 w-full items-center justify-center', className)}>
            <FontAwesomeIcon icon={faSpinner} spin className="h-4 w-4 text-gray-400" />
        </div>
    );
};
